import { RegistrationKiwiSaverFacade } from './../../registration-kiwisaver/facade/kiwisaver-registration.facade';
import { isEmptyObject } from './../../../utils/empty-object-check';
import { IAppState } from './../../state/app.state';
import * as utilitiesActions from './../../utilities/actions/utilities.actions';
import { EntityLegalType } from './../../../models/entity';
import { getTaxRateSchedule } from './../../utilities/selectors/utilities.selectors';
import { UtilitiesFacades } from 'src/app/store/utilities/facades/utilities.facades';
import { ITaxDetails } from 'src/app/models/registration/taxDetails';
import { LoadInvestorEntity } from './../actions/investor.actions';
import { getInvestorEntityDetails, getInvestorUpdateStatus } from './../selectors/investor.selectors';
import { selectRegistrantInvestorLegalEntityType } from './../../registration/selectors/investor.selectors';
import { ClientEntity } from 'src/app/models/clients';
import { Injectable } from "@angular/core";
import { Store, select } from "@ngrx/store";
import { concat, merge, Observable } from "rxjs";
import * as fromInvestor from "../selectors/investor.selectors";
import * as InvestorActions from "../actions/investor.actions";
import { IBankAccount, IInvestorEntityDetails } from "src/app/models/investor";
import { GeneralSelectionFacade } from "../../general-selection/facade/general-selection.facade";
import { tap, take, map, switchMap, filter, startWith } from "rxjs/operators";
import { combineLatest } from 'rxjs';
import { ITaxRateSelectOption, IMappedTaxRateSchedule } from 'src/app/models/tax';
import { InvestorSubType } from '../../../models/investor';
import * as investorSelectors from 'src/app/store/investor/selectors/investor.selectors';
import { IInvestorUpdateStatus } from '../../state/investor.state';
import {InvestorService} from "../../../services/http/investor.service";
import { IPortfolio } from 'src/app/models/portfolios';
import { IInvestorDetailsViewModel } from 'src/app/models/investorViewModel';
import { IMemberViewModel } from 'src/app/models/memberViewModel';

@Injectable()
export class InvestorFacade {
    constructor(
        private store: Store<IAppState>,
        private generalSelectionFacade: GeneralSelectionFacade,
        private utilitiesFacade: UtilitiesFacades,
        private kiwiSaverRegistrationFacade: RegistrationKiwiSaverFacade,
        private investorService: InvestorService) { }

    private investorEntityDetailsWithCheck$: Observable<[IInvestorEntityDetails, ClientEntity[]]> = combineLatest([
        this.store.pipe(select(getInvestorEntityDetails)),
        this.generalSelectionFacade.getSelectedInvestorEntities()
    ]).pipe(
        tap((v: [IInvestorEntityDetails, ClientEntity[]]) => {
            const [investorEntitydetails, clientEntities] = v;
            if (clientEntities && clientEntities[0] && clientEntities[0].EntityId){
                if (this.requiresInvestorEntityReload(clientEntities[0], investorEntitydetails) ) {
                    const userEntityId = clientEntities[0].EntityId;
                    if (userEntityId) {
                        this.store.dispatch(new LoadInvestorEntity(userEntityId));
                    }
                }
            }
        })
    );

    private investorEntityDetails$: Observable<IInvestorEntityDetails> = this.investorEntityDetailsWithCheck$.pipe(
        filter((v: [IInvestorEntityDetails, ClientEntity[]]) => {
            const hasInvestorEntityDetails = (v && (v[0]?.bankAccount || v[0]?.taxDetails)) ? true : false;
            return hasInvestorEntityDetails;
        }),
        map((v) => {
            return v[0];
        })
    );

    private selectLegalEntityTypeWhenLoggedIn$: Observable<number> = this.investorEntityDetails$.pipe(
        map((v: any) => {
            return v.investorDetails.legalEntityType;
        })
    );

    /**
     * startWith(0) is added to be able to combine multiple observables, even if they don't fire, which is the case here.
     * By adding startWith we make sure that each observable fires at least once, thus enabling combineLatest.
     * Because combineLatest only works if all observables fire at least once.
     */
    public legalEntityType$ = combineLatest([
        this.store.pipe(select(selectRegistrantInvestorLegalEntityType)).pipe(
            startWith(0)
        ),
        this.kiwiSaverRegistrationFacade.getKiwiSaverLegalEntityType$.pipe(
            startWith(0)
        ),
        this.selectLegalEntityTypeWhenLoggedIn$.pipe(
            startWith(0)
        )
    ]).pipe(switchMap((v) => {
        return v;
    }));

    public countryTaxResidency$ = this.investorEntityDetails$.pipe(
        map((v: IInvestorEntityDetails) => {
            return v.taxDetails.countryTaxResidency;
        })
    );

    public investorTaxDetails$ = this.investorEntityDetails$.pipe(
        map((v: IInvestorEntityDetails) => {
            return v.taxDetails;
        })
    );
    public investorDetails$ = this.investorEntityDetails$.pipe(
        map((v: IInvestorEntityDetails) => {
            return v.investorDetails;
        })
    );

    public isKiwiSaverInvestor$ = this.investorEntityDetails$.pipe(
        filter(x => x.investorDetails.subType != null),
        map((v: IInvestorEntityDetails) => {
            return v.investorDetails.subType == InvestorSubType.KiwiSaverInvestor;
        })
    );
    
    public investorStatus$ = this.investorEntityDetails$.pipe(
        map((v: IInvestorEntityDetails) => {
            return v.investorDetails.investorStatus;
        })
    )

    public investorUpdateStatus$ = this.store.pipe(
        select(getInvestorUpdateStatus),
        map((v: IInvestorUpdateStatus) => {
            return v;
        })
    );

    public taxRateSchedule$: Observable<IMappedTaxRateSchedule> = combineLatest([
        this.store.pipe(select((getTaxRateSchedule))),
        this.legalEntityType$
    ]).pipe(
        tap((v: [IMappedTaxRateSchedule, EntityLegalType]) => {
            const [taxrateSchedule, legalEntityType] = v;
            if (!!legalEntityType && isEmptyObject(taxrateSchedule)) {
                // dispatch and get the taxRateSchedule
                this.store.dispatch(new utilitiesActions.LoadTaxRateScheduleList(legalEntityType));
            }
        }),
        map((v: [IMappedTaxRateSchedule, EntityLegalType]) => {
            const [taxrateSchedule] = v;
            return taxrateSchedule;
        })
    );

    public requestTaxRateSchedule(legalEntityType: EntityLegalType) : void {
        this.store.dispatch(new utilitiesActions.LoadTaxRateScheduleList(legalEntityType));
    }

    public RWTTaxRateSchedule$: Observable<any> = this.taxRateSchedule$.pipe(
        map((t) => t.RWTTaxRateSchedule)
    );

    public PIRTaxRateSchedule$: Observable<any> = this.taxRateSchedule$.pipe(
        map((t) => t.PIRTaxRateSchedule)
    );

    public preSelectedInvestorRWTTaxRate$: Observable<[number, boolean]> = combineLatest([
        this.investorTaxDetails$,
        this.RWTTaxRateSchedule$
    ]).pipe(
        filter((v: [ITaxDetails, ITaxRateSelectOption[]]) => {
            const [taxDetails, taxRates] = v;
            return !!taxDetails?.marginalTaxRate && !!taxRates?.length;
        }),
        map((v: [ITaxDetails, ITaxRateSelectOption[]]) => {
            // check if the investor's previously provided tax rate is in accordance with the values in the available tax rates list. If not, set the default tax rate as the predefined tax rate
            const [taxDetails, taxRates] = v;
            const currentTaxrate = taxDetails.marginalTaxRate;
            const defaultTaxRate = taxRates.find((t) => !!t.isDefault);
            const defaultTaxRateValue = (defaultTaxRate) ? defaultTaxRate.value : null;
            const isInvestorProvidedTaxRateValid = (taxRates.findIndex((tr) => tr.value === currentTaxrate) > -1);
            const preSelectedTaxRate: number = isInvestorProvidedTaxRateValid ? currentTaxrate : (defaultTaxRateValue !== null ? defaultTaxRateValue : null);
            return [preSelectedTaxRate, isInvestorProvidedTaxRateValid];
        }));


    public canUserSelectTaxRate$: Observable<{ canUserSelectTaxRate: boolean, userCannotSelectTaxrateDescriptionText: string }> = this.investorTaxDetails$.pipe(
        map((taxDetails: ITaxDetails) => {
            const { taxResidentOverseas, rwtExempt } = taxDetails;
            let canUserSelectTaxRate = true;
            let userCannotSelectTaxrateDescriptionText = '';
            if (!!taxResidentOverseas || !!rwtExempt) {
                canUserSelectTaxRate = false;
                userCannotSelectTaxrateDescriptionText = this.defineUserCannotSelectTaxrateDescription(taxResidentOverseas, rwtExempt);
            }
            return {
                canUserSelectTaxRate,
                userCannotSelectTaxrateDescriptionText
            };

        })
    );

    defineUserCannotSelectTaxrateDescription(taxResidentOverseas: boolean, rwtExempt: boolean): string {
        let descriptionText = '';
        if (!!taxResidentOverseas) {
            descriptionText = "You are a foreign tax resident. There's no need for you to select a tax rate";
        } else if (!taxResidentOverseas && !!rwtExempt) {
            descriptionText = "The tax status on your account is exempt from paying RWT.  Please contact your manager or adviser if this has changed.";
        }
        return descriptionText;
    }

    getInvestorsBankAccount(): Observable<IBankAccount[]> {
        return this.store.pipe(select(fromInvestor.getInvestorBankDetails), tap((res: IBankAccount[]) => {
            if (!res) {
                this.generalSelectionFacade.getSelectedInvestorEntities()
                    .pipe(take(1))
                    .subscribe((t: ClientEntity[]) => {
                        if (t.length > 0) {
                            this.loadInvestorsBankAccount(t[0].EntityId);
                        }
                    });
            }
        }
        ));
    }
    loadInvestorsBankAccount(investorId: number) {
        this.store.dispatch(new InvestorActions.LoadInvestorBankAccount(investorId));
    }

    public saveInvestorDetails(investorDetails: any) {
        this.store.dispatch(new InvestorActions.SaveInvestorDetails(investorDetails));
    }
    public resetInvestorDetailsForm() {
        this.store.dispatch(new InvestorActions.ResetInvestorDetailsForm());
    }

    public loadInvestorPortfolios(investorId: number): Observable<IPortfolio[]> {
        return this.investorService.getInvestorPortfolios(investorId);
    }

    public getInvestorAdvisorInformation() {
        return this.store.pipe(select(investorSelectors.getInvestorAdvisorInformation));
    }

    public requestInvestorAdvisorInformation(investorEntityIds : number[]) {
        this.store.dispatch(new InvestorActions.LoadInvestorAdvisorInformation(investorEntityIds));
    }

    private requiresInvestorEntityReload(clientEntity: ClientEntity, investorEntitydetails: IInvestorEntityDetails){
        //If the entityid changed then reload the data
        if (investorEntitydetails?.investorDetails?.investorEntityId !== clientEntity.EntityId){
            return true;
        }
        // If the bank account or tax detail is missing then it is required to reload the data from the api
        if(!investorEntitydetails?.bankAccount || !investorEntitydetails?.taxDetails){
            return true;
        }

        return false;
    }

    //
    // force a reload on the currently selected investor
    public reloadCurrentlySelectedInvestor() {
        this.generalSelectionFacade.getSelectedInvestorEntities()
            .pipe(take(1))
            .subscribe((clientEntity: ClientEntity[]) => {
                this.store.dispatch(new LoadInvestorEntity(clientEntity[0].EntityId));
            });
    }

    public getInvestorDetails(investorId: number) : Observable<IInvestorDetailsViewModel> {
        return this.investorService.getInvestorDetails(investorId.toString());
    }

    public getInvestorMembers(investorId: number) : Observable<IMemberViewModel[]> {
        return this.investorService.getInvestorMembers(investorId);
    }
}